import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, User } from 'lucide-react';

interface HeaderProps {
  onSignUpClick: () => void;
  onSignInClick: () => void;
  user: any;
}

export function Header({ onSignUpClick, onSignInClick, user }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const element = document.getElementById(location.hash.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      setMobileOpenDropdown(null);
    }
  }, [mobileMenuOpen]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    if (path.includes('#')) {
      e.preventDefault();
      const [pathname, hash] = path.split('#');
      const currentPath = window.location.pathname;

      if (currentPath === pathname || pathname === '') {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.history.pushState(null, '', path);
        }
      } else {
        navigate(path);
      }
    }
  };

  const navigationItems = [
    { 
      id: 'features', 
      label: 'Features', 
      path: '/features',
      dropdown: [
        { label: 'Subplz Page Generator', path: '/features/subplz-page-generator' },
        { label: 'Automated Payments', path: '/features/automated-payments' },
        { label: 'Dashboard & Analytics', path: '/features/dashboard-analytics' },
        { label: 'Marketing Tools', path: '/features/marketing-tools' },
      ]
    },
    {
      id: 'industries',
      label: 'Industries',
      path: '/industries',
      dropdown: [
        { label: 'Home & Property Care', path: '/industries/home-property' },
        { label: 'Pet Care Services', path: '/industries/pet-care' },
        { label: 'Food & Beverage', path: '/industries/food-beverage' },
        { label: 'Health & Wellness', path: '/industries/health-wellness' },
        { label: 'Education & Skills', path: '/industries/education-skills' },
        { label: 'Automotive & Transportation', path: '/industries/automotive-transportation' },
        { label: 'Household Supplies', path: '/industries/household-supplies' },
        { label: 'Technology & Office', path: '/industries/technology-office' },
        { label: 'Personal & Lifestyle', path: '/industries/personal-lifestyle' },
        { label: 'Kids & Family', path: '/industries/kids-family' },
        { label: 'Hobby & Recreation', path: '/industries/hobby-recreation' },
        { label: 'Specialty & Niche', path: '/industries/specialty-niche' }
      ]
    },
    {
      id: 'pricing',
      label: 'Pricing',
      path: '/pricing',
      dropdown: [
        { label: 'Pricing Plans', path: '/pricing#pricing-plans' },
        { label: 'Profit Calculator', path: '/pricing/fee-calculator' },
        { label: 'Plans FAQ', path: '/pricing#faq' }
      ]
    },
    {
      id: 'resources',
      label: 'Resources',
      path: '/resources',
      dropdown: [
        { label: 'About', path: '/about' },
        { label: 'Customer Testimonials', path: '/pricing/testimonials' },
        { label: 'Blog Posts', path: '/resources#blog' },
        { label: 'Security & Compliance', path: '/security' },
        { label: 'Platform Updates', path: '/changelog' }
      ]
    },
    { id: 'contact', label: 'Contact', path: '/contact' }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-[60] pt-6 px-4 pb-6">
      <div className="max-w-fit mx-auto">
        <div className="p-[2.4px] rounded-3xl bg-gradient-to-r from-red-500 via-yellow-400 via-30% via-green-500 via-50% via-blue-500 via-70% to-violet-500">
          <div className="bg-white rounded-[21px] shadow-xl shadow-black/10">
            <div className="px-5 py-2.5">
            <div className="flex items-center justify-center gap-6">
              <div className="flex items-center gap-1">
                {/* Logo */}
                <div className="flex items-center">
                  <a href="/">
                    <img
                      src="/untitled_(420_x_160_px)_(1) copy.png"
                      alt="Subplz - Where Service Pays"
                      className="h-16 md:h-14 w-auto"
                    />
                  </a>
                  <div className="hidden md:block h-8 w-px bg-neutral-300 ml-6 mr-2"></div>
                </div>

                {/* Main Navigation - Desktop */}
                <nav className="hidden md:flex items-center">
                {navigationItems.map((item) => (
                  <div
                    key={item.id}
                    className="relative"
                    onMouseEnter={() => item.dropdown && setOpenDropdown(item.id)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <a
                      href={item.path}
                      className="px-2 py-1.5 rounded-xl font-medium transition-all duration-200 text-primary-700 hover:text-primary-900 hover:bg-white/40 flex items-center space-x-1"
                    >
                      <span>{item.label}</span>
                      {item.dropdown && (
                        <svg className="w-4 h-4 transition-transform duration-200" style={{ transform: openDropdown === item.id ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </a>
                    
                    {/* Dropdown Menu */}
                    {item.dropdown && openDropdown === item.id && (
                      <div className="absolute top-full left-0 pt-2 w-64 z-[100]">
                        <div className="bg-white/95 backdrop-blur-xl border border-white/30 rounded-2xl shadow-xl py-2">
                        {item.dropdown.map((dropdownItem, index) => (
                          <a
                            key={index}
                            href={dropdownItem.path}
                            onClick={(e) => handleNavClick(e, dropdownItem.path)}
                            className="block px-4 py-3 text-primary-700 hover:text-primary-900 hover:bg-white/80 transition-all duration-200 rounded-xl mx-2"
                          >
                            {dropdownItem.label}
                          </a>
                        ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                </nav>
              </div>

              {/* User Section */}
              <div className="flex items-center space-x-2">
                {user ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm font-medium text-primary-700">
                      {user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}
                    </span>
                  </div>
                ) : (
                  <div className="hidden md:flex items-center space-x-2">
                    <button
                      onClick={onSignInClick}
                      className="text-primary-700 hover:text-primary-900 px-4 py-2 rounded-xl font-medium transition-all duration-200 border border-neutral-300 hover:border-primary-400 hover:bg-white/40"
                    >
                      Sign In
                    </button>
                    <button
                      onClick={onSignUpClick}
                      className="bg-gradient-to-r from-accent-500 to-accent-600 text-white px-5 py-2 rounded-xl font-semibold hover:from-accent-600 hover:to-accent-700 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                    >
                      Sign Up
                    </button>
                  </div>
                )}

                {/* Mobile menu button */}
                <div className="md:hidden">
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="p-2 text-primary-700 hover:text-primary-900 hover:bg-white/40 rounded-xl transition-all duration-200"
                  >
                    {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                  </button>
                </div>
              </div>
            </div>
            
            {/* Mobile Navigation */}
            {mobileMenuOpen && (
              <div className="md:hidden mt-4 pt-4 border-t border-white/20 max-h-[calc(100vh-140px)] overflow-y-auto">
                <div className="flex flex-col space-y-2">
                  {/* Mobile Navigation Items */}
                  {navigationItems.map((item) => (
                    <div
                      key={item.id}
                    >
                      {item.dropdown ? (
                        <button
                          onClick={() => setMobileOpenDropdown(mobileOpenDropdown === item.id ? null : item.id)}
                          className="w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all duration-200 text-primary-700 hover:text-primary-900 hover:bg-white/40 flex items-center justify-between"
                        >
                          <span>{item.label}</span>
                          <svg className="w-4 h-4 transition-transform duration-200" style={{ transform: mobileOpenDropdown === item.id ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      ) : (
                        <a
                          href={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className="text-left px-4 py-2.5 rounded-xl font-medium transition-all duration-200 text-primary-700 hover:text-primary-900 hover:bg-white/40 flex items-center justify-between"
                        >
                          <span>{item.label}</span>
                        </a>
                      )}

                      {/* Mobile Dropdown */}
                      {item.dropdown && mobileOpenDropdown === item.id && (
                        <div className="ml-4 mt-2 space-y-1">
                          {item.dropdown.map((dropdownItem, index) => (
                            <a
                              key={index}
                              href={dropdownItem.path}
                              onClick={(e) => {
                                handleNavClick(e, dropdownItem.path);
                                setMobileMenuOpen(false);
                                setMobileOpenDropdown(null);
                              }}
                              className="block px-4 py-2 text-primary-600 hover:text-primary-800 hover:bg-white/70 rounded-lg transition-all duration-200 text-sm"
                            >
                              {dropdownItem.label}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {!user && (
                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          onSignInClick();
                        }}
                        className="w-full text-primary-700 hover:text-primary-900 px-4 py-2 rounded-xl font-medium transition-all duration-200 border border-neutral-300 hover:border-primary-400 hover:bg-white/40"
                      >
                        Sign In
                      </button>
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          onSignUpClick();
                        }}
                        className="w-full bg-gradient-to-r from-accent-500 to-accent-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:from-accent-600 hover:to-accent-700 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                      >
                        Sign Up
                      </button>
                    </div>
                  )}
                  
                  {user && (
                    <div className="flex items-center space-x-2 px-4 py-2.5">
                      <div className="w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm font-medium text-primary-700">
                        {user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}